import {
    Collisions,
    GameRandom,
    GameStorageManager,
    Inject,
    List,
    Singleton,
    SoundManager,
    SoundType,
} from '@denishussar/html5-game-engine';
import { ModelEvents } from '@denishussar/html5-game-engine/dist';
import {
    AppStateEnum,
    DifficultyEnum,
    GameModel,
    GameStateEnum,
    MenuStateEnum,
    TooltipStateEnum,
} from '../Models/Game/GameModel';
import { RoomModel } from '../Models/Game/RoomModel';
import { MiniGameModel, MiniGameTypeEnum } from '../Models/Game/MiniGameModel';

import TaskContants from '../Constants/TaskConstants';

import { rangeRandomInt, secondsToHHMMSS, shuffleArray } from '../Common/Utils';

import {
    TaskModel,
    TaskModelOptions,
    TaskType,
} from '../Models/Game/TaskModel';
import {
    AchievementModel,
    AchievementType,
} from '../Models/Game/AchievementModel';
import { SoundConstants } from '../Constants/SoundConstants';
import { BonusModel, BonusTypeEnum } from '../Models/Game/BonusModel';

import { BlockCardsLogic as BlockCardsLogic } from '../Controllers/Game/MiniGames/block_cards/Logic/BlockCardsLogic';
import { BussinesTripLogic as BussinesTripLogic } from '../Controllers/Game/MiniGames/bussines_trip/Logic/BussinesTripLogic';
import { ExchangeLogic as ExchangeLogic } from '../Controllers/Game/MiniGames/currency_exchange/Logic/ExchangeLogic';
import { DinnerLogic as DinnerLogic } from '../Controllers/Game/MiniGames/dinner/Logic/DinnerLogic';
import { ElectricityLogic as ElectricityLogic } from '../Controllers/Game/MiniGames/electricity/Logic/ElectricityLogic';
import { LoanLogic as LoanLogic } from '../Controllers/Game/MiniGames/loan_game/Logic/LoanLogic';
import { GameLogic as LockCellLogic } from '../Controllers/Game/MiniGames/lock_cell/Logic/LockCellLogic';
import { GameLogic as PrintCardLogic } from '../Controllers/Game/MiniGames/print_card/Logic/PrintCardLogic';
import { SendLetterLogic as SendLetterLogic } from '../Controllers/Game/MiniGames/send_letter/Logic/SendLetterLogic';

import { BonusMoneyLogic } from './Bonus/BonusMoneyLogic';
import { BonusClockLogic } from './Bonus/BonusClockLogic';
import { BonusTeaLogic } from './Bonus/BonusTeaLogic';
import { BonusEraserLogic } from './Bonus/BonusEraserLogic';
import { BonusTransformLogic } from './Bonus/BonusTransformLogic';
import { BonusCalculatorLogic } from './Bonus/BonusCalculatorLogic';
import { BonusTeleportLogic } from './Bonus/BonusTeleportLogic';
import { BonusLogoLogic } from './Bonus/BonusLogoLogic';

import BonusContants from '../Constants/BonusContants';
import { BonusLogic } from './Bonus/BonusLogic';
import DifficultyConstants from '../Constants/DifficultyConstants';
import AchievementConstants from '../Constants/AchievementConstants';
import { FirebaseAPI } from '../../FirebaseAPI';

@Singleton
export class GameLogic {
    private _bonusLogicList: List<BonusLogic>;

    public load() {
        this._storageManager.loadModel(
            '-game',
            this._model,
            true,
            true,
            data => {
                return data;
            }
        );
    }

    public referenceScreenNextPage(): void {
        if (this._model.referenceScreenCurrentPage <= 1) {
            this._model.referenceScreenCurrentPage++;
        }
    }

    public referenceScreenPrevPage(): void {
        if (this._model.referenceScreenCurrentPage > 1) {
            this._model.referenceScreenCurrentPage--;
        }
    }

    public secondsToHHMMSS(seconds: number): string {
        return secondsToHHMMSS(seconds);
    }

    public isWoman(): boolean {
        return this._model.isWoman;
    }

    public save() {
        this._storageManager.saveModel(
            '-game',
            this._model,
            {
                hiScore: true,
                lastScore: true,
                tutorialWasShow: true,
                player: {
                    guid: true,
                    name: true,
                },
            },
            true,
            true,
            data => {
                return data;
            }
        );
    }

    public setSoundOnOff(value: boolean): void {
        this._model.soundOnOff = value;
        this._soundManager.muteSoundsByType(false, SoundType.ALL);
        this._soundManager.muteSoundsByType(!value, SoundType.SFX);
    }

    public isSoundOnOff(): boolean {
        return this._model.soundOnOff;
    }

    public setMusicOnOff(value: boolean): void {
        this._model.musicOnOff = value;
        this._soundManager.muteSoundsByType(false, SoundType.ALL);
        this._soundManager.muteSoundsByType(!value, SoundType.Music);
    }

    public isMusicOnOff(): boolean {
        return this._model.musicOnOff;
    }

    constructor(
        @Inject private _model: GameModel,
        @Inject private _random: GameRandom,
        @Inject private _modelEvents: ModelEvents,
        @Inject private _storageManager: GameStorageManager,
        @Inject private _soundManager: SoundManager,
        @Inject private _collisions: Collisions,
        // MiniGames
        @Inject private BlockCardsLogic: BlockCardsLogic,
        @Inject private BussinesTripLogic: BussinesTripLogic,
        @Inject private ExchangeLogic: ExchangeLogic,
        @Inject private DinnerLogic: DinnerLogic,
        @Inject private ElectricityLogic: ElectricityLogic,
        @Inject private LoanLogic: LoanLogic,
        @Inject private LockCellLogic: LockCellLogic,
        @Inject private PrintCardLogic: PrintCardLogic,
        @Inject private SendLetterLogic: SendLetterLogic,
        // Bonuses
        @Inject private _BonusClockLogic: BonusClockLogic,
        @Inject private _BonusTeaLogic: BonusTeaLogic,
        @Inject private _BonusEraserLogic: BonusEraserLogic,
        @Inject private _BonusTransformLogic: BonusTransformLogic,
        @Inject private _BonusCalculatorLogic: BonusCalculatorLogic,
        @Inject private _BonusTeleportLogic: BonusTeleportLogic,
        @Inject private _BonusLogoLogic: BonusLogoLogic,
        @Inject private _BonusMoneyLogic: BonusMoneyLogic,

        //
        @Inject private _firebaseAPI: FirebaseAPI
    ) {
        this._bonusLogicList = new List<BonusLogic>([
            this._BonusClockLogic,
            this._BonusTeaLogic,
            this._BonusEraserLogic,
            this._BonusTransformLogic,
            this._BonusCalculatorLogic,
            this._BonusTeleportLogic,
            this._BonusLogoLogic,
            this._BonusMoneyLogic,
        ]);
    }

    public reset(): void {
        this._playerTime = null;
        this._proactivityCounter = 0;
        this._responsibilityCount = 0;
        this._doneResponsibilityGames = [];
        this._participationCount = 0;
        this._doneParticipationGames = [];
        this._lastUpdateTime = false;
        this._userGetForfeit = false;

        this._bonusLogicList.forEach(o => o.destroy());
        this._model.reset();
    }

    public init(): void {
        if (this.getDifficulty() === DifficultyEnum.none) {
            this.setDifficulty(DifficultyEnum.easy);
        }

        this._firebaseAPI.trackGameStart(this.isWoman() ? 'female' : 'male');

        this.initTasks();
        this.resetBonusTime();

        this.updatePosition();
        this.updateRooms();
        this.updatePosition();

        document.onvisibilitychange = () => {
            const unit = this._model.unitList.elementAt(0);

            unit.move[0] = 0;
            unit.move[1] = 0;
        };
    }

    public addRoom(pos: [number, number], key: string): RoomModel {
        return this._model.addRoom(pos, key);
    }

    public getRoom(key: string): RoomModel {
        return this._model.roomList.firstOrDefault(r => r.key == key);
    }

    public moveCursor(x: number, y: number): void {}

    public unitMoveStart(direction: [number, number]) {
        if (!this._model.gamePad.visible) {
            return;
        }

        const unit = this._model.unitList.firstOrDefault(ch => !!ch.player);
        if (unit) {
            unit.move = [
                direction[0] || unit.move[0],
                direction[1] || unit.move[1],
            ];
            unit.visible = true;
        }
    }

    public unitMoveStop(direction: [number, number]) {
        let unit = this._model.unitList.firstOrDefault(ch => !!ch.player);
        if (unit) {
            unit.name = this._model.player.name;
            unit.move = [
                direction[0] ? 0 : unit.move[0],
                direction[1] ? 0 : unit.move[1],
            ];
        }
    }

    public isBonusTimeEnds(): boolean {
        return this._model.bonusGenerationTime === 0;
    }

    public addBonusTime(value: number): void {
        this._model.bonusGenerationTime = Math.max(
            0,
            this._model.bonusGenerationTime + value
        );
    }

    public resetBonusTime(): void {
        this._model.bonusGenerationTime =
            this.getDifficultyConfig().bonusGenerateDelay;
    }

    private updateRooms() {
        let mapPos: [number, number] = [
            -this._model.pos[0],
            -this._model.pos[1],
        ];

        this._model.roomList.forEach(roomModel => {
            const isVisible = roomModel.checkPos(mapPos);

            roomModel.environmentList.forEach(environment => {
                environment.visible = isVisible;
                environment.pos = [
                    roomModel.pos[0] + environment.initialPos[0],
                    roomModel.pos[1] + environment.initialPos[1],
                ];
            });

            roomModel.miniGameList.forEach(miniGame => {
                miniGame.visible = isVisible;
                miniGame.pos = [
                    roomModel.pos[0] + miniGame.initialPos[0],
                    roomModel.pos[1] + miniGame.initialPos[1],
                ];
            });

            roomModel.bonusList.forEach(bonus => {
                bonus.visible = isVisible;
                bonus.pos = [
                    roomModel.pos[0] + bonus.initialPos[0],
                    roomModel.pos[1] + bonus.initialPos[1],
                ];
            });

            roomModel.borderList.forEach(border => {
                border.visible = isVisible;
                border.pos = [
                    roomModel.pos[0] + border.initialPos[0],
                    roomModel.pos[1] + border.initialPos[1],
                ];
            });

            if (isVisible !== roomModel.visible) {
                roomModel.visible = isVisible;
            }
        });
    }

    private _lastUpdateTime: boolean = false;

    private updateTime(frameRate: number): void {
        if (this._model.player.time > 0) {
            this._model.player.time = Math.max(
                0,
                this._model.player.time - frameRate
            );
        } else {
            if (this._model.player.name === '') {
                this.showGamePlayerName();
            } else {
                this.showGameEnd();
            }
        }

        if (!this.isBonusTimeEnds()) {
            this.addBonusTime(-frameRate);
        } else {
            this.pushBonus();
            this.resetBonusTime();
        }

        if (
            this._model.taskCurrentList.where(o => o.active).count() >
            this._model.taskCount
        ) {
            throw new Error('Task stack overflow!');
        }

        this._model.taskCurrentList.forEach(task => {
            if (task.active && !task.pause && task.type === TaskType.Regular) {
                if (task.time > 0) {
                    task.time = Math.max(0, task.time - frameRate);
                } else {
                    task.time = 0;
                    task.active = false;

                    this.onMiniGameTimeOver(task.miniGameType);
                }
            }
        });

        this._bonusLogicList.forEach(
            logic => logic.isActive() && logic.update(frameRate)
        );
    }

    public update(frameRate: number): void {
        if (
            this._model.showMiniGame ||
            this._model.showMiniMap ||
            this._model.showPauseScreen ||
            this._model.showFullscreen ||
            this._model.isFirstLaunch
        ) {
            return;
        }

        if (this._model.player && !this._model.showMiniMap) {
            let unit = this._model.unitList.firstOrDefault(ch => !!ch.player);

            if (unit) {
                const iterations = 10;

                const moveX =
                    (unit.move[0] * frameRate * unit.speed) / iterations;
                const moveY =
                    (unit.move[1] * frameRate * unit.speed) / iterations;

                for (let i = 0; i < iterations; i++) {
                    const pos: [number, number] = [
                        unit.pos[0] + moveX,
                        unit.pos[1] + moveY,
                    ];

                    if (unit.move[0] || unit.move[1]) {
                        unit.walk = true;
                    } else {
                        unit.walk = false;
                    }

                    const result = this._collisions.createResult();
                    unit.collision.x = pos[0];
                    unit.collision.y = pos[1];
                    this._collisions.update();

                    const potentials = unit.collision.potentials();

                    for (const body of potentials) {
                        if (unit.collision.collides(body, result)) {
                            unit.collision.x -=
                                result.overlap * result.overlap_x;
                            unit.collision.y -=
                                result.overlap * result.overlap_y;
                        }
                    }

                    pos[0] = unit.collision.x;
                    pos[1] = unit.collision.y;

                    unit.pos = pos;

                    if (unit.move[0] || unit.move[1]) {
                        this.updatePosition();
                    }
                }

                // MiniGame
                let miniGameModel = null;

                for (let i = 0; i < this._model.roomList.count(); i++) {
                    const room = this._model.roomList.elementAt(i);

                    for (let j = 0; j < room.miniGameList.count(); j++) {
                        const miniGame = room.miniGameList.elementAt(j);

                        if (miniGame.active && miniGame.check(unit.aabb)) {
                            miniGameModel = miniGame;
                            break;
                        }
                    }
                }

                const globalTaskModel = this._model.taskCurrentList.first(
                    o => o.active && o.type === TaskType.Global
                );

                const taskModel = miniGameModel
                    ? this._model.taskCurrentList.first(
                          o => o.active && o.miniGameType === miniGameModel.type
                      )
                    : null;

                let allowSelectMiniGame = !!(miniGameModel && taskModel);
                let firstShowTooltip = true;

                if (globalTaskModel) {
                    if (globalTaskModel !== taskModel) {
                        allowSelectMiniGame = false;
                    }
                }

                if (allowSelectMiniGame) {
                    if (
                        this._model.currMiniGame &&
                        this._model.currMiniGame != miniGameModel
                    ) {
                        this._model.currMiniGame.selected = false;
                    }

                    this._model.currMiniGame = miniGameModel;
                    miniGameModel.selected = true;
                } else if (this._model.currMiniGame) {
                    this._model.currMiniGame.selected = false;
                }

                // Bonus
                const bonusModel = this.getBonuses().firstOrDefault(
                    bonus =>
                        bonus.active &&
                        bonus.visible &&
                        !bonus.isCatch &&
                        bonus.type != BonusTypeEnum.none &&
                        bonus.checkPos(unit.pos)
                );

                if (bonusModel) {
                    bonusModel.isCatch = true;

                    const bonusType = bonusModel.type;
                    const roomIndex = bonusModel.roomIndex;

                    const actualBonusType = this.applyBonus(
                        bonusType,
                        roomIndex
                    );

                    this.resetBonusTime();

                    this._modelEvents.dispatch(
                        this._model,
                        'removeBonus',
                        bonusType,
                        roomIndex
                    );

                    this._modelEvents.dispatch(
                        this._model,
                        'applyBonus',
                        actualBonusType,
                        roomIndex
                    );
                }
            }
        }

        this.updateRooms();

        if (!this._model.showPauseScreen) {
            this.updateTime(frameRate);
        }
    }

    public gamePadButtonA() {
        /*
        this._model.currMiniGame = this.getMiniGameByType(MiniGameTypeEnum.currency_exchange);
        this._model.currMiniGame.selected = true;
         */

        if (this._model.currMiniGame && this._model.currMiniGame.selected) {
            switch (this._model.currMiniGame.type) {
                case MiniGameTypeEnum.block_cards:
                    this.BlockCardsLogic.reset();
                    break;

                case MiniGameTypeEnum.bussines_trip:
                    this.BussinesTripLogic.reset();
                    break;

                case MiniGameTypeEnum.currency_exchange:
                    this.ExchangeLogic.reset();
                    break;

                case MiniGameTypeEnum.dinner:
                    this.DinnerLogic.reset();
                    break;

                case MiniGameTypeEnum.electricity:
                    this.ElectricityLogic.reset();
                    break;

                case MiniGameTypeEnum.approve_the_loan:
                    this.LoanLogic.reset();
                    break;

                case MiniGameTypeEnum.lock_cell:
                    this.LockCellLogic.reset();
                    break;

                case MiniGameTypeEnum.print_card:
                    this.PrintCardLogic.reset();
                    break;

                case MiniGameTypeEnum.send_a_letter:
                    this.SendLetterLogic.reset();
                    break;
            }

            this._model.showMiniGame = true;
            this._model.gamePad.showGamePad = false;
            this._model.gamePad.showCloseButton = true;
            this._modelEvents.dispatch(
                this._model,
                'miniGameStart',
                this._model.currMiniGame.type
            );
        }
    }

    // Time
    public addTotalTime(value: number): void {
        this._model.player.time += value;
    }

    // Tasks
    public initTasks(): void {
        this.resetCompleteTaskToGetGlobalCounter();
        this.loadTasks();
        this.pushTask(true, this._model.taskCount);
    }

    private loadTasks(): void {
        this._model.taskOptionList = new List<TaskModelOptions>(
            shuffleArray(TaskContants)
        );
    }

    private addTask(options: TaskModelOptions): void {
        let taskModel = this._model.taskCurrentList.first(
            o => o.miniGameType === options.miniGameType
        );

        if (!taskModel) {
            taskModel = new TaskModel();
            this._model.taskCurrentList.add(taskModel);
        }

        const timeIndex = Object.values(DifficultyEnum).indexOf(
            DifficultyEnum[this._model.difficulty]
        );

        taskModel.type = options.type;
        taskModel.timeList = options.time;
        taskModel.time =
            options.time && options.time.length > 0
                ? options.time[timeIndex - 1]
                : 0;
        taskModel.timeoutScoreForfeit = options.timeoutScoreForfeit;
        taskModel.failScoreForfeit = options.failScoreForfeit;
        taskModel.text = options.text;
        taskModel.score = options.score;
        taskModel.miniGameType = options.miniGameType;
        taskModel.miniGameLink = null;
        taskModel.active =
            this.getActiveTasks().count() <= this._model.taskCount;
        taskModel.index =
            options.index !== -1
                ? options.index
                : this.getActiveTasks().count();

        this._modelEvents.dispatch(
            this._model,
            'addTask',
            taskModel.miniGameType
        );
    }

    public getDifficulty(): DifficultyEnum {
        return this._model.difficulty;
    }

    public setDifficulty(value: DifficultyEnum): void {
        this._model.difficulty = value;
        this._model.difficultyConfig = DifficultyConstants.find(
            o => o.type === value
        );
    }

    public getDifficultyConfig(): any {
        return this._model.difficultyConfig;
    }

    decCompleteTaskToGetGlobalCounter(): void {
        this._model.completeTaskCounter--;
    }

    isCompleteTaskToGetGlobalCounter(): boolean {
        return this._model.completeTaskCounter === 0;
    }

    resetCompleteTaskToGetGlobalCounter(): void {
        this._model.completeTaskCounter = rangeRandomInt(
            this._model.completeTaskToGetGlobal[0],
            this._model.completeTaskToGetGlobal[1]
        );
    }

    public pushTask(
        ignoreGlobal: boolean = false,
        count: number = 1,
        previousTask: TaskModel = null
    ): void {
        for (let i = 0; i < count; i++) {
            const canAddGlobalTask =
                !ignoreGlobal && this.isCompleteTaskToGetGlobalCounter();

            let task = this._model.taskOptionList
                .where(o => o.type === TaskType.Global)
                .shuffle()
                .first();

            if (!canAddGlobalTask || (canAddGlobalTask && !task)) {
                task = this._model.taskOptionList
                    .where(
                        o =>
                            o.type === TaskType.Regular &&
                            o.miniGameType !== this._model.lastMiniGameType &&
                            !this._model.taskCurrentList.first(
                                a =>
                                    a.active &&
                                    a.miniGameType === o.miniGameType
                            )
                    )
                    .shuffle()
                    .first();

                console.log(task.miniGameType, this._model.lastMiniGameType);

                this._model.lastMiniGameType = task.miniGameType;
            } else {
                this.resetCompleteTaskToGetGlobalCounter();
            }

            if (task) {
                const miniGameModel = this.getMiniGameByType(task.miniGameType);

                if (miniGameModel) {
                    miniGameModel.active = true;
                    miniGameModel.completed = false;
                    miniGameModel.played = false;
                    miniGameModel.visible = true;
                }

                if (previousTask) {
                    if (
                        this.getActiveTasks().first(
                            o => o.index === previousTask.index
                        )
                    ) {
                        debugger;
                    }

                    task.index = previousTask.index;
                } else {
                    task.index = -1;
                }

                this.addTask(task);
            }
        }
    }

    public getActiveTasks(): List<TaskModel> {
        return this._model.taskCurrentList.where(o => o.active);
    }

    public getTaskByMiniGameType(miniGameType: MiniGameTypeEnum): TaskModel {
        return this.getActiveTasks().first(
            o => o.miniGameType === miniGameType
        );
    }

    public getGlobalTask(): TaskModel {
        return this.getActiveTasks().first(
            o => o.active && o.type === TaskType.Global
        );
    }

    public setUnitPosition(miniGame: MiniGameModel): void {
        const unit = this._model.unitList.firstOrDefault();

        unit.pos = [
            miniGame.pos[0] + miniGame.teleportMargin[0],
            miniGame.pos[1] + miniGame.teleportMargin[1],
        ];

        this.updatePosition();
    }

    public updatePosition(): void {
        const unit = this._model.unitList.firstOrDefault();

        this._model.pos = [-unit.pos[0], -unit.pos[1]];
    }

    // Bonus
    public getBonuses(): List<BonusModel> {
        let bonusList = new List<BonusModel>();

        for (let i = 0; i < this._model.roomList.count(); i++) {
            bonusList = bonusList.concat(
                this._model.roomList.elementAt(i).bonusList
            );
        }

        return bonusList;
    }

    public getActiveBonuses(): List<BonusModel> {
        return this.getBonuses().where(o => o.active);
    }

    public getBonusByType(bonusType: BonusTypeEnum): BonusModel {
        return this.getBonuses().first(o => o.type === bonusType);
    }

    public getBonusByTypeAndRoom(
        bonusType: BonusTypeEnum,
        roomIndex: number
    ): BonusModel {
        return this.getBonuses().first(
            o => o.type === bonusType && o.roomIndex === roomIndex
        );
    }

    public setBonusScreenPosition(
        bonusType: BonusTypeEnum,
        roomIndex: number,
        position: [number, number]
    ): void {
        const bonus = this.getBonusByTypeAndRoom(bonusType, roomIndex);

        if (bonus) {
            if (
                position[0] !== bonus.screenPos[0] ||
                position[1] !== bonus.screenPos[1]
            ) {
                bonus.screenPos = position;
            }
        }
    }

    private pushBonus(): void {
        let freeRooms = this._model.roomList.where(
            o =>
                o.bonusList.count() > 0 &&
                o.bonusList.where(a => !a.active).count() > 0
        );
        let totalRooms = this._model.roomList.where(
            o => o.bonusList.count() > 0
        );
        let totalCount = this._model.bonusCount;

        if (totalRooms.count() - freeRooms.count() < totalCount) {
            let freeRoomIndices = [];

            for (let i = 0; i < freeRooms.count(); i++) {
                freeRoomIndices.push(freeRooms.elementAt(i).index);
            }

            const roomIndex = shuffleArray(freeRoomIndices)[0];
            const config = shuffleArray(BonusContants).find(
                o => o.rooms.indexOf(roomIndex) > -1
            );

            if (config) {
                const freeRoom = freeRooms.first(o => o.index === roomIndex);
                const freeRoomBonus = freeRoom.bonusList.first();

                freeRoomBonus.type = config.type;
                freeRoomBonus.active = true;
                freeRoomBonus.visible = freeRoom.visible;
                freeRoomBonus.isCatch = false;

                const logic = this.getBonusLogicByType(freeRoomBonus.type);
                if (logic) {
                    logic.add(this);
                }

                this._modelEvents.dispatch(
                    this._model,
                    'addBonus',
                    freeRoomBonus.type,
                    freeRoomBonus.roomIndex
                );
            } else {
                console.error(roomIndex);
            }
        }
    }

    public setBonusDoubleScoreActive(value: boolean): void {
        this._model.bonusDoubleScoreActive = value;

        const bonusModel = this.getBonusByType(BonusTypeEnum.calculator);
        if (bonusModel) {
            this._modelEvents.dispatch(
                bonusModel,
                'calculatorBonusChange',
                value
            );
        }
    }

    public isBonusDoubleScoreActive(): boolean {
        return this._model.bonusDoubleScoreActive;
    }

    public setBonusHalfScoreActive(value: boolean): void {
        this._model.bonusHalfScoreActive = value;

        const bonusModel = this.getBonusByType(BonusTypeEnum.money);
        if (bonusModel) {
            this._modelEvents.dispatch(bonusModel, 'moneyBonusChange', value);
        }
    }

    public isBonusHalfScoreActive(): boolean {
        return this._model.bonusHalfScoreActive;
    }

    public getBonusLogicByType(bonusType: BonusTypeEnum): BonusLogic {
        switch (bonusType) {
            case BonusTypeEnum.clock:
                return this._BonusClockLogic;

            case BonusTypeEnum.tea:
                return this._BonusTeaLogic;

            case BonusTypeEnum.eraser:
                return this._BonusEraserLogic;

            case BonusTypeEnum.transform:
                return this._BonusTransformLogic;

            case BonusTypeEnum.calculator:
                return this._BonusCalculatorLogic;

            case BonusTypeEnum.teleport:
                return this._BonusTeleportLogic;

            case BonusTypeEnum.logo:
                return this._BonusLogoLogic;

            case BonusTypeEnum.money:
                return this._BonusMoneyLogic;
        }

        return null;
    }

    public applyBonus(
        bonusType: BonusTypeEnum,
        roomIndex: number
    ): BonusTypeEnum {
        if (bonusType === BonusTypeEnum.money && Math.random() > 0.3) {
            const bonusModel = this.getBonusByTypeAndRoom(bonusType, roomIndex);

            bonusType = this._getRandomProp(BonusTypeEnum);

            bonusModel.type = bonusType;
        }

        console.log('applyBonus', bonusType);

        this._model.activateLogoBonus = false;

        const config = BonusContants.find(o => o.type === bonusType);
        const logic = this.getBonusLogicByType(bonusType);

        if (logic) {
            if (bonusType === BonusTypeEnum.transform) {
                if (logic.isActive()) {
                    logic.addTime(config.props.speedAddingTime);
                } else {
                    const logoLogic = this.getBonusLogicByType(
                        BonusTypeEnum.logo
                    );

                    if (logoLogic.isActive()) {
                        const logoConfig = BonusContants.find(
                            o => o.type === BonusTypeEnum.logo
                        );

                        logoLogic.addTime(logoConfig.props.speedAddingTime);

                        /*
                        const logoSpeedAddingPercent =
                            logoConfig.props.speedAddingPercent;
                        const transformSpeedAddingPercent =
                            config.props.speedAddingPercent;

                        if (
                            logoSpeedAddingPercent > transformSpeedAddingPercent
                        ) {
                            logoLogic.addTime(logoConfig.props.speedAddingTime);
                        } else {
                            logoLogic.cancel();
                            logic.apply(this);
                        }
                         */
                    } else {
                        logic.apply(this);
                    }
                }
            } else if (bonusType === BonusTypeEnum.logo) {
                if (logic.isActive()) {
                    logic.addTime(config.props.speedAddingTime);
                } else {
                    const transformLogic = this.getBonusLogicByType(
                        BonusTypeEnum.transform
                    );

                    if (transformLogic.isActive()) {
                        const transformConfig = BonusContants.find(
                            o => o.type === BonusTypeEnum.logo
                        );

                        transformLogic.addTime(
                            transformConfig.props.speedAddingTime
                        );

                        /*
                        const transformSpeedAddingPercent =
                            transformConfig.props.speedAddingPercent;
                        const logoSpeedAddingPercent =
                            config.props.speedAddingPercent;

                        if (
                            transformSpeedAddingPercent > logoSpeedAddingPercent
                        ) {
                            transformLogic.addTime(
                                transformConfig.props.speedAddingTime
                            );
                        } else {
                            transformLogic.cancel();
                            logic.apply(this);
                        }
                         */
                    } else {
                        logic.apply(this);
                    }
                }
            } else {
                logic.apply(this);
            }
        }

        this.showBonusTooltip(bonusType, config.text);

        return bonusType;
    }

    private _getRandomProp(obj: object) {
        const keys = Object.keys(obj);
        const list = new List(keys.slice(1, keys.length));

        return BonusTypeEnum[list.shuffle().first()]
    }

    // MiniGame
    public getMiniGames(): List<MiniGameModel> {
        let miniGameList = new List<MiniGameModel>();

        for (let i = 0; i < this._model.roomList.count(); i++) {
            miniGameList = miniGameList.concat(
                this._model.roomList.elementAt(i).miniGameList
            );
        }

        return miniGameList;
    }

    public getActiveMiniGames(): List<MiniGameModel> {
        return this.getMiniGames().where(o => o.active);
    }

    public getMiniGameByType(miniGameType: MiniGameTypeEnum): MiniGameModel {
        return this.getMiniGames().first(o => o.type === miniGameType);
    }

    public setMiniGameScreenPosition(
        miniGameType: MiniGameTypeEnum,
        position: [number, number]
    ): void {
        const miniGame = this.getMiniGameByType(miniGameType);

        if (miniGame) {
            miniGame.screenPos = position;
        }
    }

    private _userGetForfeit: boolean = false;
    public onMiniGameComplete(miniGameType: MiniGameTypeEnum): void {
        const miniGameModel = this.getMiniGameByType(miniGameType);
        if (miniGameModel) {
            miniGameModel.completed = true;
            miniGameModel.played = true;

            const taskModel = this._model.taskCurrentList.first(
                o => o.miniGameType === miniGameType
            );

            if (taskModel) {
                taskModel.active = false;

                let addingScore = taskModel.score;

                // FIXME: to bonus logic
                if (this.isBonusDoubleScoreActive()) {
                    addingScore *= 2;
                    this.setBonusDoubleScoreActive(false);
                }

                if (this.isBonusHalfScoreActive()) {
                    addingScore /= 2;
                    this.setBonusHalfScoreActive(false);
                }
                const forfeit =
                    taskModel.failScoreForfeit +
                    (taskModel.failScoreForfeit *
                        this.getDifficultyConfig().scoreAddingPercent) /
                        100;

                const difficultyScore =
                    addingScore +
                    (addingScore *
                        this.getDifficultyConfig().scoreAddingPercent) /
                        100;

                if (this._userGetForfeit) {
                    this._model.addScore(difficultyScore - forfeit);
                    this.showTooltip(
                        TooltipStateEnum.task,
                        `Ты успешно выполнил задание "${
                            taskModel.text
                        }" +${Math.ceil(difficultyScore - forfeit)} очков!`
                    );
                } else {
                    this._model.addScore(difficultyScore);
                    this.showTooltip(
                        TooltipStateEnum.task,
                        `Ты успешно выполнил задание "${
                            taskModel.text
                        }" +${Math.ceil(difficultyScore)} очков!`
                    );
                }

                this._modelEvents.dispatch(
                    this._model,
                    'miniGameComplete',
                    miniGameType
                );
                this._modelEvents.dispatch(
                    this._model,
                    'removeTask',
                    miniGameType
                );

                if (taskModel.type === TaskType.Regular) {
                    this.decCompleteTaskToGetGlobalCounter();
                }

                this.pushTask(taskModel.type === TaskType.Global, 1, taskModel);
            } else {
                throw new Error(
                    'GameLogic::onMiniGameComplete() Cant found task for miniGame ' +
                        miniGameType
                );
            }

            if (this._model.currMiniGame === miniGameModel) {
                this._model.currMiniGame.selected = false;
                this._model.currMiniGame = null;
            }

            this._model.showMiniGame = false;
            this._model.currMiniGame = null;
            this._model.gamePad.showGamePad = true;
            this._model.gamePad.showCloseButton = false;

            if (!this._model.showMiniGame) {
                this._userGetForfeit = false;
            }
        } else {
            throw new Error(
                'GameLogic::onMiniGameComplete() Cant found miniGame for miniGame ' +
                    miniGameType
            );
        }
    }

    public onMiniGameFail(miniGameType: MiniGameTypeEnum): void {
        const miniGameModel = this.getMiniGameByType(miniGameType);
        console.log('onMiniGameFail');
        if (miniGameModel) {
            const taskModel = this._model.taskCurrentList.first(
                o => o.miniGameType === miniGameType
            );

            if (taskModel) {
                this._userGetForfeit = true;
            } else {
                throw new Error(
                    'GameLogic::onMiniGameTimeOver() Cant found task for miniGame ' +
                        miniGameType
                );
            }
            ++miniGameModel.failCount;
            this._soundManager.play(SoundConstants.BUTTON_CLICK);
            this._modelEvents.dispatch(
                this._model,
                'miniGameFail',
                miniGameType,
                ++miniGameModel.failCount
            );
        }
    }

    public onMiniGameTimeOver(miniGameType: MiniGameTypeEnum): void {
        if (this._model.currMiniGame) {
            this._model.currMiniGame.selected = false;
        }

        const miniGameModel = this.getMiniGameByType(miniGameType);

        if (miniGameModel) {
            miniGameModel.active = false;
            miniGameModel.played = false;

            const taskModel = this._model.taskCurrentList.first(
                o => o.miniGameType === miniGameType
            );

            if (taskModel) {
                taskModel.active = false;

                /*
                this._model.addScore(
                    -(
                        taskModel.timeoutScoreForfeit +
                        (taskModel.timeoutScoreForfeit *
                            this.getDifficultyConfig().scoreAddingPercent) /
                            100
                    )
                );
                 */
                this._modelEvents.dispatch(
                    this._model,
                    'miniGameTimeOver',
                    miniGameType
                );
                this._modelEvents.dispatch(
                    this._model,
                    'removeTask',
                    miniGameType
                );

                this.pushTask(taskModel.type === TaskType.Global, 1, taskModel);
            } else {
                throw new Error(
                    'GameLogic::onMiniGameTimeOver() Cant found task for miniGame ' +
                        miniGameType
                );
            }

            if (this._model.currMiniGame === miniGameModel) {
                this._model.currMiniGame.selected = false;
                this._model.currMiniGame = null;
            }

            this._model.showMiniGame = false;
            this._model.currMiniGame = null;
            this._model.gamePad.showGamePad = true;
            this._model.gamePad.showCloseButton = false;
        } else {
            throw new Error(
                'GameLogic::onMiniGameTimeOver() Cant found miniGame for miniGame ' +
                    miniGameType
            );
        }
    }

    // Achievements
    public isAchievements(
        type: AchievementType,
        miniGame: MiniGameTypeEnum
    ): void {
        const achievementsNotDone = this._model.achievementList.where(
            o => !o.done
        );

        if (achievementsNotDone.count()) {
            const achievementModel = achievementsNotDone.first(
                o => o.type === type
            );

            if (achievementModel) {
                switch (type) {
                    case AchievementType.Proactivity:
                        this._achievementProactivity(
                            AchievementType.Proactivity,
                            achievementModel
                        );
                        break;
                    case AchievementType.Openness:
                        this._achievementOpenness(
                            AchievementType.Openness,
                            achievementModel
                        );
                        break;
                    case AchievementType.Responsibility:
                        this._achievementResponsibility(
                            AchievementType.Responsibility,
                            achievementModel,
                            miniGame
                        );
                        break;
                    case AchievementType.Participation:
                        this._achievementParticipation(
                            AchievementType.Participation,
                            achievementModel,
                            miniGame
                        );
                        break;
                }
            }
        }
    }

    private _playerTime: number = null;
    private _proactivityCounter: number = 0;

    private _achievementProactivity(
        type: AchievementType,
        model: AchievementModel
    ): void {
        if (this._proactivityCounter < 1) {
            this._playerTime = this._model.player.time;
        }
        if (!(Math.abs(this._playerTime - this._model.player.time) >= 60)) {
            this._proactivityCounter++;
        }

        if (this._proactivityCounter >= 4 && !model.done) {
            model.done = true;
            this._model.collectAchievements.push(type);
            this._showAchievement(type);
        }
    }

    private _achievementOpenness(
        type: AchievementType,
        model: AchievementModel
    ): void {
        if (!model.done) {
            model.done = true;
            this._model.collectAchievements.push(type);
            this._showAchievement(type);
        }
    }

    public achievementOpennessFail(): void {
        this._model.opennessFailed = true;
    }

    private _responsibilityCount: number = 0;
    private _doneResponsibilityGames = [];
    private _achievementResponsibility(
        type: AchievementType,
        model: AchievementModel,
        miniGame: MiniGameTypeEnum
    ): void {
        if (this._doneResponsibilityGames.indexOf(miniGame) === -1) {
            this._doneResponsibilityGames.push(miniGame);
            this._responsibilityCount++;
        }

        if (this._responsibilityCount === 6 && !model.done) {
            model.done = true;
            this._model.collectAchievements.push(type);
            this._showAchievement(type);
        }
    }

    private _participationCount: number = 0;
    private _doneParticipationGames = [];
    private _achievementParticipation(
        type: AchievementType,
        model: AchievementModel,
        miniGame: MiniGameTypeEnum
    ): void {
        if (this._doneParticipationGames.indexOf(miniGame) === -1) {
            this._doneParticipationGames.push(miniGame);
            this._participationCount++;
        }

        if (this._participationCount === 3 && !model.done) {
            model.done = true;
            this._model.collectAchievements.push(type);
            this._showAchievement(type);
        }
    }

    // Tooltip
    public showBonusTooltip(type: BonusTypeEnum, text: string): void {
        this.showTooltip(TooltipStateEnum.bonus, text, type);
    }

    private _showAchievement(type: AchievementType) {
        this.showTooltip(
            TooltipStateEnum.achievement,
            `Ты получил достижение '${AchievementConstants[type].text}'!`,
            type
        );
    }

    public showTooltip(
        state: TooltipStateEnum,
        text: string,
        icon: string = ''
    ): void {
        this._modelEvents.dispatch(this._model, 'showTooltip', {
            tooltipText: text,
            tooltipState: state,
            tooltipIcon: icon,
        });
    }

    public hideTooltip(): void {
        this._modelEvents.dispatch(this._model, 'hideTooltip');
    }

    // Screen
    public showMenuScreen(): void {
        this._model.appState = AppStateEnum.menu;
    }

    public showGameScreen(): void {
        this._model.appState = AppStateEnum.game;
    }

    public showSaveScreen(): void {
        this._model.appState = AppStateEnum.save;
    }

    public resetGameScreen(): void {
        this._model.appState = AppStateEnum.none;
    }

    // Pause
    public showGamePlayerName(): void {
        this._model.showPauseScreen = true;
        this._model.gameState = GameStateEnum.playerName;
    }

    public showGamePause(): void {
        this._model.showPauseScreen = true;
        this._model.gameState = GameStateEnum.pause;
    }

    public resetGamePause(): void {
        this._model.showPauseScreen = false;
        this._model.isFirstLaunch = false;
        this._model.gameState = GameStateEnum.none;
    }

    public showPauseSettings(): void {
        this._model.gameState = GameStateEnum.settings;
    }

    public showPauseRules(): void {
        this._model.gameState = GameStateEnum.rules;
    }

    public showPauseAchievements(): void {
        this._model.gameState = GameStateEnum.achievements;
    }

    public showPauseToMenu(): void {
        this._model.gameState = GameStateEnum.restartToMenu;
    }

    public showPauseRestart(): void {
        this._model.gameState = GameStateEnum.restart;
    }

    public showGameEnd(): void {
        this._model.showPauseScreen = true;
        this._model.gameState = GameStateEnum.gameEnd;
    }

    public resetGame(): void {
        this._model.showMiniMap = false;
        this._model.gameState = GameStateEnum.none;
    }

    public showGameMiniMap(): void {
        this._model.showMiniMap = true;
        this._model.gameState = GameStateEnum.minimap;
    }

    public showGameLeaderboards(): void {
        this._model.gameState = GameStateEnum.leaderboards;
    }

    // Menu
    public resetMenu(): void {
        this._model.menuState = MenuStateEnum.none;
    }

    public showMenuSelectDifficulty(): void {
        this._model.menuState = MenuStateEnum.selectDifficulty;
    }

    public showMenuSelectCharacter(): void {
        this._model.menuState = MenuStateEnum.selectCharacter;
    }

    public showMenuSettings(): void {
        this._model.menuState = MenuStateEnum.settings;
    }

    public showMenuAchievements(): void {
        this._model.menuState = MenuStateEnum.achievements;
    }

    public showMenuLeaderboards(): void {
        this._model.menuState = MenuStateEnum.leaderboards;
    }

    public showMenuRules(): void {
        this._model.menuState = MenuStateEnum.rules;
    }

    // Firebase
    public async initStorage(): Promise<void> {
        const currentId = this._storageManager.loadData('currentId');

        if (currentId !== null && typeof currentId !== 'undefined') {
            this._model.player.id = currentId;
        } else {
            this._model.player.id = '';
        }
    }

    public async initDB(): Promise<void> {
        this._firebaseAPI.init();
    }

    public async loadDB(): Promise<void> {
        const id = this._model.player.id;

        const data = await this._firebaseAPI.load(id);

        if (data) {
            console.log(
                `found userdata ${this._model.player.id} ${JSON.stringify(
                    data
                )}`
            );

            this._model.player.name = data.name;
            this._model.player.totalScore = data.score;
            this._model.collectAchievements = data.achievements;

            for (let i = 0; i < this._model.collectAchievements.length; i++) {
                const collectAchievement = this._model.collectAchievements[i];

                const achievement = this._model.achievementList.first(
                    o => o.type === collectAchievement
                );

                if (achievement) {
                    achievement.done = true;
                }
            }
        } else {
            console.log(`userdata not found`);
            this._model.player.name = '';
        }
    }

    public async saveDB(name: string): Promise<void> {
        const id = this._model.player.id;
        const score = this._model.player.score;
        const totalScore = this._model.player.totalScore;
        const achievements = this._model.collectAchievements;

        if ((score === 0 && totalScore === 0) || score > totalScore) {
            console.log(id, name, score, totalScore);

            const currentId = await this._firebaseAPI.save(id, {
                name: name,
                score: score,
                achievements: achievements,
            });

            this._firebaseAPI.trackGameEnd(this.isWoman() ? 'female' : 'male');

            this._model.player.name = name;
            this._model.player.id = currentId;
            this._model.player.totalScore = score;

            this._storageManager.saveData('currentId', currentId);
        }
    }

    public async getLeaderboards(): Promise<any> {
        const users = await this._firebaseAPI.getUsers();

        const array = Array.from(users)
            .map(o => {
                return { ...o[1], id: o[0] };
            })
            .sort((a, b) => b.score - a.score)

        return array;
    }
}
